import { useEffect, useState, useContext, useMemo } from 'react';
import {
    Container,
    Grid,
    CircularProgress,
    Typography,
    Button,
    Box,
    Alert as MuiAlert,
} from '@mui/material';
import { withStyles } from '@mui/styles';
import { useHistory } from 'react-router-dom';
import { FormProvider, useFieldArray, useForm, Controller } from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import FlagIcon from '@mui/icons-material/Flag';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import ExpenseButtons from '../Components/Expense/ExpenseButtons';
import ExpenseReceiptUploadForm from '../Components/Expense/ExpenseReceiptUploadForm';
import ExpenseEntryHeader from '../Components/Expense/ExpenseEntryHeader';
import {
    createExpense,
    updateExpense,
    getExpenseById,
    getExpenseCategories,
    getStatusesAllowed,
    patchExpenseCreditMemo,
} from '../Services/ExpenseService';
import { getDivisions } from '../Services/Common';
import { formatErrorMessage } from '../Utils/ErrorMessage';
import { uploadImages } from '../Utils/UploadImages';
import Alert from '../Components/Alert';
import ExpenseTabs from '../Components/Expense/Tabs/ExpenseTabs';
import ExpenseTab from '../Components/Expense/Tabs/ExpenseTab';
import ExpenseTabLabel from '../Components/Expense/Tabs/ExpenseTabLabel';
import AddExpenseTab from '../Components/Expense/Tabs/AddExpenseTab';
import ExpenseTabPanel from '../Components/Expense/Tabs/ExpenseTabPanel';
import BookingDetailsForm from '../Components/Expense/BookingDetailsForm';
import ExpenseSalesforceActionButton from '../Components/Expense/ExpenseSalesforceActionButton';
import InvoicePriorityIndicator from '../Components/Expense/InvoicePriorityIndicator';
import ClinicianExpenseDetailsForm from '../Components/Expense/ClinicianExpenseDetailsForm';
import ExpenseSummary from '../Components/Expense/ExpenseSummary';
import InternalNotesForm from '../Components/Expense/InternalNotesForm';
import ReasonForSplittingForm from '../Components/Expense/ReasonForSplittingForm';
import { ExpenseStatusValuesEnum } from '../Components/Expense/ExpenseStatus.enum';
import { ExpenseClassEnum } from '../Components/Expense/ExpenseClass.enum';
import { BillTypeEnum } from '../Components/Expense/BillType.enum';
import ExpenseNeedsReviewAlert from '../Components/Expense/ExpenseNeedsReviewAlert';
import { COLORS } from '../../utils/Application_Constants';
import { AdminAuthContext } from '../../Admin/Contexts/AdminAuthContext';
import { currencies } from '../Configurations/ExpenseEntryConfig';
import {
    createCorrectionExpenseFromEntries,
    createExpenseFromExpenseEntries,
    isExpenseViewOnlyByStatus,
    isExpenseInInvoiceReview,
    shouldEnableByCorrectionAndApprovedStatus,
    handleSplitExpenseDeltas,
} from '../Utils/ExpenseUtil';
import { ExpenseModeEnum } from '../Components/Expense/ExpenseMode.enum';
import eventBus from '../Components/Expense/EventBus';
import CorrectionsAmountNoteBox from '../Components/Expense/CorrectionsAmountNoteBox';
import { createExpenseDelta } from '../Utils/ExpenseDelta';
import { ExpenseCategoryEnum } from '../Components/Expense/ExpenseCategory.enum';
import { ExpenseTypeEnum } from '../Components/Expense/ExpenseType.enum';
import ExpenseCorrectionDialog from '../Components/Dialog/ExpenseCorrectionDialog';
import ExpenseCorrectionAlert from '../Components/Expense/ExpenseCorrectionAlert';
import CreditDeterminationDialog from '../Components/Dialog/CreditDeterminationDialog';
import PayrollDayWarningDialog from '../Components/Dialog/PayrollDayWarningDialog';
import AssociatedInvoiceDocuments from '../Components/Expense/AssociatedInvoiceDocuments';
import { useToast } from '../../Admin/Contexts/ToastContext';
import usePayrollDayApprovalWarning from '../hooks/usePayrollDayApprovalWarning';

const styles = (theme) => ({
    ContainerRoot: {
        [theme.breakpoints.down('md')]: {
            paddingLeft: '16px',
            paddingRight: '16px',
        },
        paddingTop: '1.5rem',
        paddingBottom: '5rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '40px',
    },
    formGridContainer: {
        gap: 32,
    },
    summary: {
        position: 'sticky',
        top: '5rem',
        paddingTop: '.5rem',
    },
    warningMessage: {
        color: COLORS.LT_SUNFLOWER,
        paddingTop: '0.5rem',
    },
    iconContainer: {
        position: 'absolute',
        top: 8,
        right: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    flagIcon: {
        cursor: 'pointer',
    },
    viewBookingButton: {
        minWidth: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
    },
    tabPanel: {
        position: 'relative',
    },
    alertContainer: {
        marginTop: theme.spacing(4),
        [theme.breakpoints.down('sm')]: {
            marginTop: theme.spacing(4),
        },
    },
    leftIconContainer: {
        position: 'absolute',
        top: 8,
        left: 8,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        zIndex: 10,
    },
    editButton: {
        backgroundColor: COLORS.LT_JOURNEY10,
        '&:hover': {
            backgroundColor: COLORS.LT_JOURNEY25,
        },
        color: COLORS.LT_JOURNEY,
    },
});

const castNullValuesToEmptyString = (objectParam) =>
    Object.fromEntries(
        Object.entries(objectParam).map(([key, value]) => [key, value === null ? '' : value])
    );

const defaultExpense = {
    bookingId: null,
    clinicianId: '',
    clinicianFirstName: '',
    clinicianLastName: '',
    clientId: '',
    division: '',
    state: '',
    billType: '',
    category: '',
    type: '',
    currency: 'USD',
    amount: '',
    reimbursedAmount: '',
    accountingProcessingNotes: '',
    label: 'Expense',
    mileage: '',
    status: ExpenseStatusValuesEnum.New,
    salespersonName: '',
    recruiterName: '',
    payDate: null,
    expenseClass: ExpenseClassEnum.Reimbursement,
    splitTransactionComment: '',
    source: 'LtAssociate',
    clinicianFacingComments: '',
    commentsFromClinician: '',
    merchantName: '',
    highPriority: false,
    isInvoicePriority: false,
    timelineToInvoiceExpense: null,
};

const ClinicianReimbursement = (props) => {
    const { classes } = props;
    const flags = useFlags();
    const { ltoeWebExpensesEnableEditButtonClinicianReimbursement: enableEditButtonByFF } = flags;
    const [isLoading, setIsLoading] = useState(false);
    const { isExpenseSystemAdministratorUser, isExpenseAdminUser, isClinicianReimEntryUser } =
        useContext(AdminAuthContext);
    const isViewOnly = !(
        isExpenseSystemAdministratorUser ||
        isExpenseAdminUser ||
        isClinicianReimEntryUser
    );
    const [expenseCategories, setExpenseCategories] = useState(null);
    const [divisions, setDivisions] = useState([]);
    const [statusesAllowed, setStatusesAllowed] = useState([]);
    const [statusesAllowedByIndex, setStatusesAllowedByIndex] = useState({});
    const { id } = useParams();
    const history = useHistory();
    const [alert, setAlert] = useState();
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [removeTabs, setRemoveTabs] = useState(false);
    const backLink = id ? 'All Expenses' : 'Expense Types';
    const title = id ? 'Clinician Reimbursement' : 'Clinician Reimbursement Expense Entry';
    const expensesPath = id ? '/expenses/review' : '/expenses';
    const [originalExpenseEntries, setOriginalExpenseEntries] = useState([]);
    const [originalPrimaryExpense, setOriginalPrimaryExpense] = useState();
    const [ocrUsageLogs, setOcrUsageLogs] = useState([]);
    const [ocrUsedValues, setUsedOcrValues] = useState([]);
    const [isAnyExpensePosted, setIsAnyExpensePosted] = useState(false);
    const defaultValues = {
        fileUploads: [{ newFiles: [], existingFiles: [] }],
        expenseEntries: [defaultExpense],
    };
    const methods = useForm({ shouldUnregister: false, defaultValues });
    const {
        handleSubmit,
        reset,
        control,
        getValues,
        setValue,
        setError,
        trigger,
        clearErrors,
        unregister,
        watch,
    } = methods;
    const {
        fields: expenseEntries, // not reactive, do not get updates, just to handle indexes
        append: appendExpenseEntry,
        remove: removeExpenseEntryAtIndex,
    } = useFieldArray({
        control, // control props comes from useForm (optional: if you are using FormContext)
        name: 'expenseEntries', // unique name for your Field Array
        keyName: 'expenseEntryId',
    });
    const watchExpenseEntries = watch('expenseEntries'); // reactive field array
    const activeExpense = watchExpenseEntries?.[activeTabIndex];
    const activeExpenseFieldPath = `expenseEntries[${activeTabIndex}]`;

    const isAnyApproved = useMemo(
        () =>
            Object.values(watchExpenseEntries).some(
                ({ status }) => status === ExpenseStatusValuesEnum.Approved
            ),
        [watchExpenseEntries]
    );

    const isOutdated = activeExpense?.status === ExpenseStatusValuesEnum.Outdated;
    const isCorrection = activeExpense?.status === ExpenseStatusValuesEnum.InCorrection;
    // An expense in correction is editable again, so the add-split (+) tab has to stay
    // available. The posted/pending checks that hide it are derived from the
    // originally-loaded snapshot, which still reads Posted until the correction is saved,
    // so also look at the live form status - otherwise the user has to save the correction
    // and reopen the details page before they can add a split.
    const isActiveExpenseInCorrection =
        isCorrection ||
        originalExpenseEntries[activeTabIndex]?.status === ExpenseStatusValuesEnum.InCorrection;
    const isPendingSubmission = activeExpense?.status === ExpenseStatusValuesEnum.PendingSubmission;
    const isReadyForReview = activeExpense?.status === ExpenseStatusValuesEnum.ReadyForReview;
    const isInvoiceReviewLocked = isExpenseInInvoiceReview(activeExpense);
    const isPageViewOnly =
        isViewOnly ||
        isOutdated ||
        isPendingSubmission ||
        (isClinicianReimEntryUser && activeExpense?.id && isReadyForReview);

    const [showBalanceError, setShowBalanceError] = useState(false);
    const [showBalanceWarning, setShowBalanceWarning] = useState(false);
    const hasErrors = !!Object.keys(methods?.formState?.errors).length;
    const [assignmentsByIndex, setAssignmentsByIndex] = useState({});
    const [openConfirmationEditModal, setOpenConfirmationEditModal] = useState(false);
    const [enableEditByModalByIndex, setEnableEditByModalByIndex] = useState({});
    const [enableEditByCorrection, setEnableEditByCorrection] = useState(false);
    const [showAmountNoteBox, setShowAmountNoteBox] = useState(false);
    const [openCreditMemoDialog, setOpenCreditMemoDialog] = useState(false);
    const [isPatchingCreditMemo, setIsPatchingCreditMemo] = useState(false);
    const [creditMemoError, setCreditMemoError] = useState(null);
    const {
        payrollWarningOpen,
        showPayrollWarningInCmm,
        payrollWarningMessage,
        gateApproval,
        confirmPayrollWarning,
        cancelPayrollWarning,
    } = usePayrollDayApprovalWarning({ creditMemoDialogOpen: openCreditMemoDialog });
    const { showErrorToast } = useToast();

    const originalReimbursedAmountTotal = useMemo(
        () =>
            originalExpenseEntries.reduce(
                (acc, { originalReimbursedAmount }) =>
                    acc + (Number(originalReimbursedAmount) || 0),
                0
            ),
        [originalExpenseEntries]
    );
    const currentReimbursedAmountTotal = useMemo(
        () =>
            watchExpenseEntries.reduce(
                (acc, { reimbursedAmount }) => acc + (Number(reimbursedAmount) || 0),
                0
            ),
        [watchExpenseEntries]
    );
    // Calculate if correction amount warning should be shown
    const shouldShowCorrectionWarning =
        isCorrection && originalReimbursedAmountTotal !== currentReimbursedAmountTotal;

    // A correction is being approved when an entry that was loaded as InCorrection is
    // finalized. Its live status flips to Approved at approval time, so rely on the
    // originally-loaded snapshot (plus the live status while still editing) to keep the
    // correction context available for the $0.00 balance validation in ExpenseSummary.
    const isCorrectionApproval = useMemo(
        () =>
            isCorrection ||
            originalExpenseEntries.some(
                (entry) => entry?.status === ExpenseStatusValuesEnum.InCorrection
            ),
        [isCorrection, originalExpenseEntries]
    );

    const handleAssignmentChange = (index, assignment) => {
        setAssignmentsByIndex((prev) => ({ ...prev, [index]: assignment }));
    };

    useEffect(() => {
        const isAnyExpensePostedCheck = (entries) => {
            if (!Array.isArray(entries) || entries.length === 0) return false;
            setIsAnyExpensePosted(
                entries.some(({ status }) => status === ExpenseStatusValuesEnum.Posted)
            );
        };

        const fetchExpenseById = async () => {
            try {
                setIsLoading(true);
                const res = await getExpenseById(id);
                const listExpenses = [];
                const aOriginalExpenseEntries = [];
                const aFileUploads = [];
                const allowedStatusesByIdx = {};
                let selectedTabIndex = 0;

                if (res.expenseClass !== ExpenseClassEnum.Reimbursement) {
                    window.location.href = '#/404';
                    showErrorToast('Clinician Reimbursement not found.');
                    return;
                }

                if (res.primaryExpenseId && res.primaryExpense) {
                    // child split
                    listExpenses.push(res.primaryExpense);

                    if (res.primaryExpense.allowedNextStatuses) {
                        allowedStatusesByIdx[0] = res.primaryExpense.allowedNextStatuses;
                    }

                    for (let i = 0; i < res.primaryExpense.splitExpenses.length; i++) {
                        const listExpensesIndex = listExpenses.push(
                            res.primaryExpense.splitExpenses[i]
                        );

                        if (res.primaryExpense.splitExpenses[i].allowedNextStatuses) {
                            allowedStatusesByIdx[listExpensesIndex - 1] =
                                res.primaryExpense.splitExpenses[i].allowedNextStatuses;
                        }

                        if (Number(res.primaryExpense.splitExpenses[i].id) === Number(id)) {
                            selectedTabIndex = listExpensesIndex - 1;
                        }
                    }
                } else {
                    // Reimbursement expenseVersion is unreliable (can be null or 0), so unlike
                    // the credit card page we can't gate on it. Always attempt to fetch a newer
                    // (corrected) version; getExpenseById 404s when none exists (handled below).
                    try {
                        const newerVersion = await getExpenseById(id, true);

                        if (newerVersion?.id) {
                            res.newerVersionID = newerVersion.id;
                        }
                    } catch (error) {
                        if (error.response && error.response.status !== 404) {
                            console.error('Error fetching newer version:', error);
                        }
                    }

                    listExpenses.push(res);

                    if (res.allowedNextStatuses) {
                        allowedStatusesByIdx[0] = res.allowedNextStatuses;
                    }

                    for (let i = 0; i < res.splitExpenses.length; i++) {
                        const listExpensesIndex = listExpenses.push(res.splitExpenses[i]);

                        if (res.splitExpenses[i].allowedNextStatuses) {
                            allowedStatusesByIdx[listExpensesIndex - 1] =
                                res.splitExpenses[i].allowedNextStatuses;
                        }
                    }
                }

                listExpenses[0].splitExpenses = [];

                for (let i = 0; i < listExpenses.length; i++) {
                    const formattedValues = castNullValuesToEmptyString(listExpenses[i]);
                    // Reset payDate back to Null as empty string on the as the default was causing issues
                    formattedValues.payDate =
                        typeof formattedValues.payDate === 'string' &&
                        formattedValues.payDate.length === 0
                            ? null
                            : formattedValues.payDate.substring(0, 10);
                    aOriginalExpenseEntries.push({
                        ...formattedValues,
                        label: listExpenses[i].label ? listExpenses[i].label : `Expense ${i}`,
                    });
                    if (
                        listExpenses[i] &&
                        listExpenses[i].expenseImages &&
                        listExpenses[i].expenseImages.length >= 0
                    ) {
                        aFileUploads.push({
                            newFiles: [],
                            existingFiles: listExpenses[i].expenseImages,
                        });
                    } else {
                        aFileUploads.push({ newFiles: [], existingFiles: [] });
                    }
                }
                setOriginalExpenseEntries(aOriginalExpenseEntries);
                setOriginalPrimaryExpense(aOriginalExpenseEntries[0]);
                setStatusesAllowedByIndex(allowedStatusesByIdx);
                setActiveTabIndex(selectedTabIndex);
                isAnyExpensePostedCheck(aOriginalExpenseEntries);
                // Set new form defaultValues
                reset({ fileUploads: aFileUploads, expenseEntries: aOriginalExpenseEntries });
            } catch (error) {
                console.error('Error fetching expense by ID:', error);
            } finally {
                setIsLoading(false);
            }
        };

        Promise.all([getDivisions(), getExpenseCategories(ExpenseClassEnum.Reimbursement)])
            .then(async ([divs, cats]) => {
                setExpenseCategories(cats);
                setDivisions(divs);
                if (!id) {
                    const statuses = await getStatusesAllowed(ExpenseClassEnum.Reimbursement);
                    setStatusesAllowed(statuses);
                }
            })
            .catch((error) => {
                console.error(error);
            });

        if (id) {
            fetchExpenseById();
        }
    }, []);

    useEffect(() => {
        if (watchExpenseEntries.length > 1) {
            const firstExpenseComment = watchExpenseEntries[0]?.commentsFromClinician || '';

            watchExpenseEntries.forEach((expense, idx) => {
                if (idx !== 0 && expense.commentsFromClinician !== firstExpenseComment) {
                    setValue(`expenseEntries[${idx}].commentsFromClinician`, firstExpenseComment);
                }
            });
        }
    }, [watchExpenseEntries]);

    useEffect(() => {
        if (!activeExpense) return;
        setEnableEditByCorrection(shouldEnableByCorrectionAndApprovedStatus(activeExpense));
    }, [activeExpense]);

    const removeUploadsAtIndex = (index) => {
        // Resort/Reset Images
        const trimmedFileUploads = [...getValues('fileUploads')];
        trimmedFileUploads.splice(index, 1);
        setValue('fileUploads', trimmedFileUploads, { shouldDirty: true, shouldValidate: false });
    };

    // This was added to address a rendering issue
    // It turns off the TabPanel Display removes the item and then turns back on the display
    useEffect(() => {
        if (removeTabs) {
            const idx = activeTabIndex;
            removeUploadsAtIndex(idx);
            removeExpenseEntryAtIndex(idx);
            unregister([`expenseEntries[${idx}]`, `fileUploads[${idx}]`]);
            clearErrors(['expenseEntries', 'fileUploads']);
            // Reset form to new values
            reset({
                expenseEntries: getValues('expenseEntries'),
                fileUploads: getValues('fileUploads'),
            });
            setActiveTabIndex((prev) => Math.max(prev - 1, 0));
            setRemoveTabs(false);
        }
    }, [removeTabs]);

    const autoFillWithOCR = (ocrData) => {
        const confidenceThreshold = 0.7;
        const expenseVals = getValues(activeExpenseFieldPath);

        let usedTransactionDate = false;
        let usedMerchantName = false;
        let usedAmount = false;
        let usedCurrency = false;

        if (
            !expenseVals.transactionDate &&
            !!ocrData.transactionDate?.date &&
            ocrData.transactionDate?.confidence >= confidenceThreshold
        ) {
            // Without this, the transaction date is off by one day visually (still saves correctly)
            const dateOffByOneCorrected = moment
                .utc(ocrData.transactionDate?.date)
                .format('YYYY-MM-DD');
            expenseVals.transactionDate = dateOffByOneCorrected;
            eventBus.emit(`txnDateChanged${activeTabIndex}`, {
                data: dateOffByOneCorrected,
            });
            usedTransactionDate = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                transactionDate: ocrData.transactionDate?.date,
            };

            setUsedOcrValues((prevOcrUsedValues) => [...prevOcrUsedValues, ocrVal]);
        }

        if (
            !expenseVals.merchantName &&
            !!ocrData.merchantName?.name &&
            ocrData.merchantName?.confidence >= confidenceThreshold
        ) {
            expenseVals.merchantName = ocrData.merchantName?.name;
            usedMerchantName = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                merchantName: ocrData.merchantName?.name,
            };

            setUsedOcrValues((prevOcrUsedValues) => [...prevOcrUsedValues, ocrVal]);
        }

        if (
            !expenseVals.amount &&
            !!ocrData.expenseAmount?.amount &&
            ocrData.expenseAmount?.confidence >= confidenceThreshold
        ) {
            expenseVals.amount = ocrData.expenseAmount?.amount;

            if (currencies.some((c) => c.value === ocrData.expenseAmount?.currencyCode)) {
                expenseVals.currency = ocrData.expenseAmount?.currencyCode;
                usedCurrency = true;
            } else {
                expenseVals.currency = 'USD'; // Default to USD on unsupported currency code
                usedCurrency = false;
            }

            usedAmount = true;

            const ocrVal = {
                scanId: ocrData.scanId,
                amount: ocrData.expenseAmount?.amount,
                currency: ocrData.expenseAmount?.currencyCode,
            };

            setUsedOcrValues((prevOcrUsedValues) => [...prevOcrUsedValues, ocrVal]);
        }

        const ocrUsageLogForScan = {
            expenseId: -1,
            scanId: ocrData.scanId,
            fieldUsage: {
                transactionDate: usedTransactionDate,
                merchantName: usedMerchantName,
                amount: usedAmount,
                currency: usedCurrency,
            },
        };

        setOcrUsageLogs((prevOcrUsageLogs) => [...prevOcrUsageLogs, ocrUsageLogForScan]);
        setValue(activeExpenseFieldPath, expenseVals);
    };

    const getOcrUsage = (expense) =>
        ocrUsageLogs.map((usageLog) => {
            const newUsageLog = { ...usageLog };
            const scanUsedValues = ocrUsedValues.filter(
                (usedValue) => usedValue.scanId === usageLog.scanId
            );

            if (
                usageLog.fieldUsage.transactionDate &&
                scanUsedValues.find((val) => !!val.transactionDate).transactionDate !==
                    expense.transactionDate
            ) {
                newUsageLog.fieldUsage.transactionDate = false;
            }

            if (
                usageLog.fieldUsage.merchantName &&
                scanUsedValues.find((val) => !!val.merchantName).merchantName !==
                    expense.merchantName
            ) {
                newUsageLog.fieldUsage.merchantName = false;
            }

            if (
                newUsageLog.fieldUsage.amount &&
                scanUsedValues.find((val) => !!val.amount).amount !== expense.amount
            ) {
                newUsageLog.fieldUsage.amount = false;
            }

            if (
                newUsageLog.fieldUsage.currency &&
                scanUsedValues.find((val) => !!val.currency).currency !== expense.currency
            ) {
                newUsageLog.fieldUsage.currency = false;
            }

            return {
                ...newUsageLog,
                fieldUsage: JSON.stringify(newUsageLog.fieldUsage),
            };
        });

    const onSubmit = async (data) => {
        try {
            setIsLoading(true);
            const newExpense = createExpenseFromExpenseEntries(data.expenseEntries);
            newExpense.ocrFieldUsage = getOcrUsage(newExpense);
            const expenseResponse = await createExpense(newExpense);
            const fileUploads = getValues(`fileUploads`);

            try {
                await uploadImages(
                    fileUploads,
                    expenseResponse,
                    ExpenseModeEnum.ClinicianReimbursement
                );

                history.push({
                    pathname: '/expenses/submit',
                    state: {
                        newExpenseUrl: `/expenses/clinician-reimbursement`,
                        listViewUrl: '/expenses/review',
                        type: 'submit',
                        expenseUrl: `/expenses/clinician-reimbursement/${expenseResponse.id}`,
                        isViewOnly: false,
                    },
                });
            } catch (uploadError) {
                console.error(
                    'Error onSubmit Clinician Reimbursement Expense Entry for image upload: ',
                    uploadError
                );
                const message = `Expense was created, but image upload failed. Error message: ${formatErrorMessage(
                    uploadError
                )}`;
                setAlert({ message, type: 'error' });
            }
        } catch (error) {
            console.error('Error onSubmit Clinician Reimbursement Expense Entry');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({ message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const patchCreditMemo = async (creditType) => {
        try {
            const expenseId = originalExpenseEntries[activeTabIndex].id;
            setCreditMemoError(null);
            await patchExpenseCreditMemo(expenseId, creditType === 'full' ? 'Full' : 'Partial');
            history.push({
                pathname: '/expenses/submit',
                state: {
                    listViewUrl: '/expenses/review',
                    expenseUrl: `/expenses/clinician-reimbursement/${expenseId}`,
                    type: 'save',
                    isViewOnly: true,
                },
            });
        } catch (error) {
            console.error('Failed to update expense credit memo', error);
            setCreditMemoError('Failed to update expense credit memo');
            const message = `Expense was saved and image was uploaded, delta was created, but updating the credit memo failed. Error message: ${formatErrorMessage(
                error
            )}`;
            setAlert({ message, type: 'error' });
        } finally {
            setIsPatchingCreditMemo(false);
        }
    };

    const onSave = async (data, setCreditMemo = undefined) => {
        try {
            if (!setCreditMemo) setIsLoading(true);

            let entries = data.expenseEntries;
            const anyInCorrection = entries.some(
                (e) => e.status === ExpenseStatusValuesEnum.InCorrection
            );
            if (anyInCorrection) {
                entries = createCorrectionExpenseFromEntries(entries);
            }
            const updatedExpense = createExpenseFromExpenseEntries(entries);
            updatedExpense.ocrFieldUsage = getOcrUsage(updatedExpense);
            const expenseResponse = await updateExpense(updatedExpense.id, updatedExpense);
            const fileUploads = getValues(`fileUploads`);

            try {
                await uploadImages(
                    fileUploads,
                    expenseResponse,
                    ExpenseModeEnum.ClinicianReimbursement,
                    true
                );

                try {
                    await handleSplitExpenseDeltas(
                        expenseResponse,
                        originalExpenseEntries,
                        createExpenseDelta
                    );
                    let expenseUrlId = expenseResponse.id;
                    let expenseStatus = expenseResponse.status;
                    if (activeTabIndex > 0) {
                        if (
                            updatedExpense.splitExpenses &&
                            updatedExpense.splitExpenses.length > 0 &&
                            updatedExpense.splitExpenses[activeTabIndex - 1]?.expenseTabName
                        ) {
                            const tabName =
                                updatedExpense.splitExpenses[activeTabIndex - 1].expenseTabName;
                            const splitExpense = expenseResponse.splitExpenses.find(
                                (se) => se.expenseTabName === tabName
                            );

                            expenseUrlId = splitExpense.id;
                            expenseStatus = splitExpense.status;
                        } else {
                            expenseUrlId = expenseResponse.splitExpenses[activeTabIndex - 1]?.id;
                            expenseStatus =
                                expenseResponse.splitExpenses[activeTabIndex - 1]?.status;
                        }
                    }
                    if (setCreditMemo) {
                        console.log('Setting credit memo for expense', expenseUrlId, setCreditMemo);
                        await patchCreditMemo(setCreditMemo);
                        return;
                    }

                    return history.push({
                        pathname: '/expenses/submit',
                        state: {
                            listViewUrl: '/expenses/review',
                            expenseUrl: `/expenses/clinician-reimbursement/${expenseUrlId}`,
                            type: 'save',
                            isViewOnly: [
                                ExpenseStatusValuesEnum.Approved,
                                ExpenseStatusValuesEnum.Posted,
                                ExpenseStatusValuesEnum.Invoiced,
                            ].includes(expenseStatus),
                        },
                    });
                } catch (deltaError) {
                    console.error(
                        'Error onSave Clinician Reimbursement Expense Entry for delta creation: ',
                        deltaError
                    );
                    const message = `Expense was saved and image was uploaded, but delta creation failed. Error message: ${formatErrorMessage(
                        deltaError
                    )}`;
                    setAlert({ message, type: 'error' });
                }
            } catch (uploadError) {
                console.error(
                    'Error onSave Clinician Reimbursement Expense Entry for image upload: ',
                    uploadError
                );
                const message = `Expense was saved, but image upload failed. Any new deltas were not created. Error message: ${formatErrorMessage(
                    uploadError
                )}`;
                setAlert({ message, type: 'error' });
            }
        } catch (error) {
            console.error('Error onSave Clinician Reimbursement Expense Entry');
            reset({ expenseEntries: data.expenseEntries, fileUploads: data.fileUploads });
            const message = formatErrorMessage(error);
            setAlert({ message, type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const clearExpenseHiddenErrors = (expense, index) => {
        // Field 1: Transaction Date  transactionDate
        if (!expense.category) {
            clearErrors(`expenseEntries[${index}].transactionDate`);
        }

        // Field 2: Start Date  startDate
        // Field 3: End Date  endDate
        if (
            expense.expenseClass === 3 ||
            expense.expenseClass === ExpenseClassEnum.SalesGeneralAndAdmin
        ) {
            if (
                !(
                    expense.category === ExpenseCategoryEnum.Other &&
                    expense.type === ExpenseTypeEnum.LodgingSecurityDeposit
                )
            ) {
                clearErrors(`expenseEntries[${index}].startDate`);
                clearErrors(`expenseEntries[${index}].endDate`);
            }
        } else if (
            !(
                expense.category === ExpenseCategoryEnum.Travel &&
                !(
                    expense.type === ExpenseTypeEnum.BaggageFee ||
                    expense.type === ExpenseTypeEnum.Fuel ||
                    !expense.type
                )
            )
        ) {
            clearErrors(`expenseEntries[${index}].startDate`);
            clearErrors(`expenseEntries[${index}].endDate`);
        }

        // Field 4: Delivery Date  deliveryDate
        // if (expense.status !== ExpenseStatusValuesEnum.Approved) {
        //     clearErrors(`expenseEntries[${index}].deliveryDate`);
        // }

        // Field 5: Pay Date(Credit Card Transaction)  payDate
        if (
            !(
                expense.expenseClass === ExpenseClassEnum.Reimbursement ||
                (expense.billType === BillTypeEnum.ClinicianDeduction &&
                    expense.expenseClass === ExpenseClassEnum.IngestedCCTransaction)
            )
        ) {
            clearErrors(`expenseEntries[${index}].payDate`);
        }

        // Field 6: Mileage  mileage
        if (
            !(
                expense.category === ExpenseCategoryEnum.Travel &&
                expense.type === ExpenseTypeEnum.Mileage
            )
        ) {
            clearErrors(`expenseEntries[${index}].mileage`);
        }

        // Field 7:  On Hold Reason
        if (expense.status !== ExpenseStatusValuesEnum.OnHoldReimbursement) {
            clearErrors(`expenseEntries[${index}].onHoldComment`);
        }

        // Field 8 : Misc Description
        if (
            expense.category !== ExpenseCategoryEnum.Misc ||
            expense.type !== ExpenseTypeEnum.Misc
        ) {
            clearErrors(`expenseEntries[${index}].miscDescription`);
        }
    };

    const clearAllHiddenErrors = () => {
        watchExpenseEntries.forEach(clearExpenseHiddenErrors);
    };

    const validateSubmit = async () => {
        clearAllHiddenErrors();

        // const allFieldsOk = await validateAllRegisteredFields();
        const allFieldsOk = await trigger(undefined, { shouldFocus: true });
        console.log('allFieldsOk', allFieldsOk);

        if (!allFieldsOk) return;

        handleSubmit(onSubmit)();
    };

    const validateSave = async (setCreditMemo = undefined) => {
        clearAllHiddenErrors();

        // const allFieldsOk = await validateAllRegisteredFields();
        const allFieldsOk = await trigger(undefined, { shouldFocus: true });

        if (!allFieldsOk) {
            setOpenCreditMemoDialog(false);
            setIsPatchingCreditMemo(false);
            return;
        }

        if (methods?.formState?.errors?.balance) {
            setShowBalanceError(true);
            if (setCreditMemo) {
                setOpenCreditMemoDialog(false);
                setIsPatchingCreditMemo(false);
            }
            return;
        }
        clearErrors('balance');
        setShowBalanceError(false);

        const intercepted = gateApproval({
            expenses: getValues('expenseEntries'),
            originalExpenses: originalExpenseEntries,
            isCreditMemoFlow: Boolean(setCreditMemo),
            proceed: () => handleSubmit((data) => onSave(data, setCreditMemo))(),
        });

        if (intercepted && setCreditMemo) {
            setIsPatchingCreditMemo(false);
        }
    };

    function handleFlagClick(onChange, value) {
        if (isPageViewOnly || isClinicianReimEntryUser) {
            return;
        }

        return onChange(!value);
    }

    const handleAddExpense = () => {
        const newExpense = {
            ...getValues('expenseEntries[0]'),
            label: 'New Expense',
            reimbursedAmount: '',
            billType: '',
            id: null,
            created: null,
            lastModified: null,
            status: ExpenseStatusValuesEnum.New,
            source: 'LtAssociate', // This will Ensure any Split from a source: 'Clinician' will be changed to 'LtAssociate'
            highPriority: false,
            isInvoicePriority: false,
            timelineToInvoiceExpense: null,
            resubmittedDate: null,
            submittedDate: null,
            mobileSubmission: false,
            salespersonName: '',
            recruiterName: '',
            description: '',
            expenseVersion: 0,
            previousVersionID: null,
        };

        // Create independent file uploads while preserving parent files for user decision
        const parentFileUpload = getValues('fileUploads[0]');
        const newFileUpload = {
            // Clone existing database files
            existingFiles:
                parentFileUpload.existingFiles && parentFileUpload.existingFiles.length > 0
                    ? parentFileUpload.existingFiles
                          .filter((file) => !file.isCombinedPdf)
                          .map((file) => ({
                              ...file, // Create new object reference
                              expenseId: null, // Clear out the existing ExpenseId
                          }))
                    : [],
            // Clone newly uploaded files so user can decide to keep or remove
            newFiles:
                parentFileUpload.newFiles && parentFileUpload.newFiles.length > 0
                    ? parentFileUpload.newFiles.map((file) => {
                          const cloneNewFile = new File([file], file.name, { type: file.type });
                          Object.assign(cloneNewFile, {
                              preview: URL.createObjectURL(file),
                          });
                          return cloneNewFile;
                      })
                    : [],
        };

        // Add the new file upload structure to the form
        const fileUploads = getValues(`fileUploads`);
        fileUploads.push(newFileUpload);
        setValue(`fileUploads`, fileUploads);

        // Clean up expense object
        if (Object.prototype.hasOwnProperty.call(newExpense, 'errors')) {
            delete newExpense.errors;
        }
        if (Object.prototype.hasOwnProperty.call(newExpense, 'statusHistory')) {
            delete newExpense.statusHistory;
        }
        if (Object.prototype.hasOwnProperty.call(newExpense, 'relatedErrors')) {
            delete newExpense.relatedErrors;
        }
        if (Object.prototype.hasOwnProperty.call(newExpense, 'importedData')) {
            delete newExpense.importedData;
        }
        if (!newExpense.splitTransactionComment) {
            newExpense.splitTransactionComment = '';
        }

        appendExpenseEntry(newExpense);
        setActiveTabIndex(expenseEntries.length);
    };

    const handleRemoveExpense = (index) => {
        // This was added to address a rendering issue
        // It turns off the TabPanel Display removes the item and then turns back on the display
        setActiveTabIndex(index);
        setRemoveTabs(true);
    };

    const enableCorrectionEditMode = () => {
        setOpenConfirmationEditModal(false);
        setEnableEditByModalByIndex((prev) => ({ ...prev, [activeTabIndex]: true }));
        const currentVersion = activeExpense?.expenseVersion;
        const updatedVersion = currentVersion ? currentVersion + 1 : 1;
        setValue(`${activeExpenseFieldPath}.expenseVersion`, updatedVersion);
        setValue(`${activeExpenseFieldPath}.status`, ExpenseStatusValuesEnum.InCorrection, {
            shouldDirty: true,
        });
    };

    useEffect(() => {
        if (!enableEditButtonByFF) return setShowAmountNoteBox(false);

        if (isCorrection) return setShowAmountNoteBox(true);

        if (
            originalExpenseEntries[activeTabIndex]?.status === ExpenseStatusValuesEnum.InCorrection
        ) {
            return setShowAmountNoteBox(true);
        }

        return setShowAmountNoteBox(false);
    }, [enableEditButtonByFF, isCorrection, activeTabIndex, originalExpenseEntries]);

    useEffect(() => {
        if (!openCreditMemoDialog) setCreditMemoError(null);
    }, [openCreditMemoDialog]);

    const onCloseCreditMemoDialog = (_event, reason) => {
        if (['escapeKeyDown', 'backdropClick'].includes(reason)) return;

        return setOpenCreditMemoDialog(false);
    };

    const handleConfirmCreditMemoDialog = async (creditTypeSelected) => {
        setIsPatchingCreditMemo(true);
        validateSave(creditTypeSelected);
    };

    return (
        <Container maxWidth="xl" className={classes.ContainerRoot}>
            <FormProvider {...methods}>
                <Grid container direction="column" spacing={2}>
                    <ExpenseEntryHeader
                        title={title}
                        backLink={backLink}
                        expensesPath={expensesPath}
                    />
                    {isLoading ? (
                        <CircularProgress color="primary" />
                    ) : (
                        <>
                            <Grid item xs={12}>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <Grid container spacing={4}>
                                        <Grid item sm={7} md={8} lg={9} xs={12}>
                                            <ExpenseTabs value={activeTabIndex}>
                                                {expenseEntries.map((expense, index) => (
                                                    <ExpenseTab
                                                        key={expense.expenseEntryId}
                                                        id={`expense-tab-${index}`}
                                                        label={
                                                            <ExpenseTabLabel
                                                                isActive={activeTabIndex === index}
                                                                index={index}
                                                                handleRemoveTab={() =>
                                                                    handleRemoveExpense(index)
                                                                }
                                                                setValue={setActiveTabIndex}
                                                                tab={expense}
                                                                mode={
                                                                    ExpenseModeEnum.ClinicianReimbursement
                                                                }
                                                                isViewOnly={isPageViewOnly}
                                                                originalPrimaryExpense={
                                                                    originalPrimaryExpense
                                                                }
                                                                isAnyExpensePosted={
                                                                    isAnyExpensePosted
                                                                }
                                                            />
                                                        }
                                                    />
                                                ))}
                                                {!isPageViewOnly &&
                                                    (isActiveExpenseInCorrection ||
                                                        !(
                                                            isAnyExpensePosted ||
                                                            (originalPrimaryExpense &&
                                                                originalPrimaryExpense.status ===
                                                                    ExpenseStatusValuesEnum.PendingSubmission)
                                                        )) &&
                                                    !(
                                                        watchExpenseEntries[0].status ===
                                                            ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                        originalPrimaryExpense?.status ===
                                                            ExpenseStatusValuesEnum.NeedMoreInformation
                                                    ) && (
                                                        <AddExpenseTab
                                                            label={<AddIcon color="primary" />}
                                                            disabled={
                                                                watchExpenseEntries[0].status ===
                                                                    ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                                originalPrimaryExpense?.status ===
                                                                    ExpenseStatusValuesEnum.NeedMoreInformation ||
                                                                expenseEntries.length === 5 ||
                                                                (isExpenseViewOnlyByStatus(
                                                                    originalExpenseEntries[
                                                                        activeTabIndex
                                                                    ],
                                                                    { isClinicianReimEntryUser }
                                                                ) &&
                                                                    isExpenseViewOnlyByStatus(
                                                                        watchExpenseEntries[
                                                                            activeTabIndex
                                                                        ],
                                                                        { isClinicianReimEntryUser }
                                                                    ))
                                                            }
                                                            onClick={handleAddExpense}
                                                            id="expense-tab-add"
                                                        />
                                                    )}
                                            </ExpenseTabs>
                                            {!removeTabs && (
                                                <div>
                                                    {expenseEntries.map((expense, index) => (
                                                        <ExpenseTabPanel
                                                            key={expense.expenseEntryId}
                                                            className={classes.tabPanel}
                                                            value={activeTabIndex}
                                                            index={index}
                                                            id={`expense-panel-${index}`}
                                                        >
                                                            <Box
                                                                className={
                                                                    classes.leftIconContainer
                                                                }
                                                            >
                                                                {enableEditButtonByFF &&
                                                                    isExpenseViewOnlyByStatus(
                                                                        originalExpenseEntries[
                                                                            index
                                                                        ],
                                                                        { isClinicianReimEntryUser }
                                                                    ) &&
                                                                    !enableEditByModalByIndex[
                                                                        index
                                                                    ] &&
                                                                    !isOutdated && (
                                                                        <Button
                                                                            variant="contained"
                                                                            className={
                                                                                classes.editButton
                                                                            }
                                                                            color="primary"
                                                                            onClick={() =>
                                                                                setOpenConfirmationEditModal(
                                                                                    true
                                                                                )
                                                                            }
                                                                            disabled={
                                                                                isPageViewOnly ||
                                                                                isClinicianReimEntryUser
                                                                            }
                                                                        >
                                                                            Edit
                                                                        </Button>
                                                                    )}
                                                            </Box>
                                                            <Box className={classes.iconContainer}>
                                                                <ExpenseSalesforceActionButton
                                                                    assignment={
                                                                        assignmentsByIndex[index]
                                                                    }
                                                                    expense={
                                                                        watchExpenseEntries[
                                                                            index
                                                                        ] || expense
                                                                    }
                                                                    originalClinicianId={
                                                                        originalExpenseEntries[
                                                                            index
                                                                        ]?.clinicianId
                                                                    }
                                                                    className={
                                                                        classes.viewBookingButton
                                                                    }
                                                                />
                                                                <InvoicePriorityIndicator
                                                                    isInvoicePriority={
                                                                        expense.isInvoicePriority
                                                                    }
                                                                    timelineToInvoiceExpense={
                                                                        expense.timelineToInvoiceExpense
                                                                    }
                                                                    fontSize="large"
                                                                />
                                                                <Controller
                                                                    name={`expenseEntries[${index}].highPriority`}
                                                                    control={control}
                                                                    defaultValue={
                                                                        expense.highPriority
                                                                    }
                                                                    render={({
                                                                        onChange,
                                                                        value,
                                                                    }) => (
                                                                        <FlagIcon
                                                                            fontSize="large"
                                                                            className={
                                                                                classes.flagIcon
                                                                            }
                                                                            onClick={() =>
                                                                                handleFlagClick(
                                                                                    onChange,
                                                                                    value
                                                                                )
                                                                            }
                                                                            style={{
                                                                                color: value
                                                                                    ? COLORS.LT_PEPPER
                                                                                    : COLORS.LT_MIDNIGHT25,
                                                                            }}
                                                                        />
                                                                    )}
                                                                />
                                                            </Box>

                                                            <div className={classes.alertContainer}>
                                                                <ExpenseCorrectionAlert
                                                                    expense={expense}
                                                                    mode={
                                                                        ExpenseModeEnum.ClinicianReimbursement
                                                                    }
                                                                />
                                                                {expense.errors &&
                                                                    expense.errors.length > 0 && (
                                                                        <ExpenseNeedsReviewAlert
                                                                            expense={expense}
                                                                        />
                                                                    )}
                                                            </div>

                                                            <Grid
                                                                container
                                                                direction="column"
                                                                className={
                                                                    classes.formGridContainer
                                                                }
                                                            >
                                                                <Grid item xs={12}>
                                                                    <BookingDetailsForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        divisions={divisions}
                                                                        isSGA={[]}
                                                                        isViewOnly={
                                                                            isPageViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ],
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            ) &&
                                                                                !enableEditByModalByIndex[
                                                                                    index
                                                                                ])
                                                                        }
                                                                        isInvoiceReviewLocked={
                                                                            isInvoiceReviewLocked
                                                                        }
                                                                        onAssignmentChange={
                                                                            handleAssignmentChange
                                                                        }
                                                                        enableEditByCorrection={
                                                                            enableEditByCorrection
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <ExpenseReceiptUploadForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        mode={
                                                                            ExpenseModeEnum.ClinicianReimbursement
                                                                        }
                                                                        isViewOnly={
                                                                            isPageViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ],
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            ) &&
                                                                                !enableEditByModalByIndex[
                                                                                    index
                                                                                ])
                                                                        }
                                                                        isInvoiceReviewLocked={
                                                                            false
                                                                        }
                                                                        onOCRUpload={
                                                                            autoFillWithOCR
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <ClinicianExpenseDetailsForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        expenseCategories={
                                                                            expenseCategories
                                                                        }
                                                                        mode={
                                                                            ExpenseModeEnum.ClinicianReimbursement
                                                                        }
                                                                        isViewOnly={
                                                                            isPageViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ],
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            ) &&
                                                                                !enableEditByModalByIndex[
                                                                                    index
                                                                                ] &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                        isInvoiceReviewLocked={
                                                                            isInvoiceReviewLocked
                                                                        }
                                                                        enableEditByCorrection={
                                                                            enableEditByModalByIndex[
                                                                                index
                                                                            ]
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <InternalNotesForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        statusesAllowed={
                                                                            statusesAllowedByIndex[
                                                                                index
                                                                            ] || statusesAllowed
                                                                        }
                                                                        mode={
                                                                            ExpenseModeEnum.ClinicianReimbursement
                                                                        }
                                                                        isReimbursement
                                                                        isViewOnly={
                                                                            isPageViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ],
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            ) &&
                                                                                !enableEditByModalByIndex[
                                                                                    index
                                                                                ] &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                        numTabs={
                                                                            expenseEntries.length
                                                                        }
                                                                        isCorrection={isCorrection}
                                                                        openCreditMemoDialog={() =>
                                                                            setOpenCreditMemoDialog(
                                                                                true
                                                                            )
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <ReasonForSplittingForm
                                                                        index={index}
                                                                        expense={expense}
                                                                        originalExpenseEntries={
                                                                            originalExpenseEntries
                                                                        }
                                                                        isReimbursement
                                                                        isViewOnly={
                                                                            isPageViewOnly ||
                                                                            (isExpenseViewOnlyByStatus(
                                                                                originalExpenseEntries[
                                                                                    index
                                                                                ],
                                                                                {
                                                                                    isClinicianReimEntryUser,
                                                                                }
                                                                            ) &&
                                                                                !enableEditByModalByIndex[
                                                                                    index
                                                                                ] &&
                                                                                !enableEditByCorrection)
                                                                        }
                                                                        mode={
                                                                            ExpenseModeEnum.ClinicianReimbursement
                                                                        }
                                                                    />
                                                                </Grid>
                                                            </Grid>
                                                        </ExpenseTabPanel>
                                                    ))}
                                                </div>
                                            )}
                                        </Grid>
                                        <Grid item md={4} sm={5} lg={3} xs={12}>
                                            <div className={classes.summary}>
                                                <ExpenseSummary
                                                    setError={setError}
                                                    clearErrors={clearErrors}
                                                    setShowBalanceError={setShowBalanceError}
                                                    showBalanceError={showBalanceError}
                                                    setShowBalanceWarning={setShowBalanceWarning}
                                                    isAnyApproved={isAnyApproved}
                                                    isCorrection={isCorrectionApproval}
                                                    mode={ExpenseModeEnum.ClinicianReimbursement}
                                                />
                                                {showBalanceError && (
                                                    <Typography color="error">
                                                        {methods?.formState?.errors?.balance
                                                            ?.message ?? ''}
                                                    </Typography>
                                                )}
                                                {showBalanceWarning && (
                                                    <Typography
                                                        className={classes.warningMessage}
                                                        color="inherit"
                                                    >
                                                        Warning: The reimbursed amount exceeds the
                                                        submitted amount. Please review the values
                                                        before saving or submitting.
                                                    </Typography>
                                                )}
                                                {shouldShowCorrectionWarning && (
                                                    <Typography className={classes.warningMessage}>
                                                        Warning: The new reimbursement amount
                                                        differs from the original. Please review the
                                                        values before saving.
                                                    </Typography>
                                                )}
                                                {showAmountNoteBox && (
                                                    <CorrectionsAmountNoteBox
                                                        mode={
                                                            ExpenseModeEnum.ClinicianReimbursement
                                                        }
                                                        isSupplemental={false}
                                                        originalAmount={
                                                            originalReimbursedAmountTotal
                                                        }
                                                        newAmount={currentReimbursedAmountTotal}
                                                    />
                                                )}
                                                {activeExpense?.id && (
                                                    <Box sx={{ marginTop: 4 }}>
                                                        <AssociatedInvoiceDocuments
                                                            expenseId={activeExpense.id}
                                                        />
                                                    </Box>
                                                )}
                                            </div>
                                        </Grid>
                                    </Grid>
                                </form>
                            </Grid>
                            {hasErrors > 0 && (
                                <Grid container className={classes.alertContainer}>
                                    <Grid item sm={7} md={8} lg={9} xs={12}>
                                        <MuiAlert severity="error">
                                            Please review the highlighted fields above and correct
                                            the errors to submit your expense.
                                        </MuiAlert>
                                    </Grid>
                                </Grid>
                            )}
                            {!(
                                expenseEntries.length === 1 &&
                                watchExpenseEntries[0].status ===
                                    ExpenseStatusValuesEnum.PendingSubmission
                            ) &&
                                !isPageViewOnly && (
                                    <Grid item xs={12}>
                                        <ExpenseButtons
                                            validateSubmit={validateSubmit}
                                            validateSave={() => validateSave()}
                                            expensesPath={id ? '/expenses/review' : '/expenses'}
                                            enableEditByModal={
                                                enableEditByModalByIndex[activeTabIndex]
                                            }
                                        />
                                    </Grid>
                                )}
                        </>
                    )}
                </Grid>
            </FormProvider>
            <Alert alert={alert} setAlert={setAlert} />

            <ExpenseCorrectionDialog
                open={openConfirmationEditModal}
                onClose={() => {
                    setOpenConfirmationEditModal(false);
                }}
                onConfirm={enableCorrectionEditMode}
                expenseId={originalExpenseEntries[activeTabIndex]?.id}
                isFullCredit={originalExpenseEntries[activeTabIndex]?.isfullCredit}
            />
            {watchExpenseEntries[activeTabIndex]?.status ===
                ExpenseStatusValuesEnum.InvoiceReview && (
                <CreditDeterminationDialog
                    open={openCreditMemoDialog}
                    onClose={onCloseCreditMemoDialog}
                    onConfirm={handleConfirmCreditMemoDialog}
                    expense={watchExpenseEntries[activeTabIndex]}
                    isLoading={isPatchingCreditMemo}
                    error={creditMemoError}
                    confirmButtonText="Save and Confirm"
                    payrollDayWarning={showPayrollWarningInCmm ? payrollWarningMessage : null}
                />
            )}
            <PayrollDayWarningDialog
                open={payrollWarningOpen}
                message={payrollWarningMessage}
                onCancel={cancelPayrollWarning}
                onConfirm={confirmPayrollWarning}
            />
        </Container>
    );
};

export default withStyles(styles)(ClinicianReimbursement);
